/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef, useImperativeHandle, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Image,
    DrawerHeader,
    useBreakpointValue,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerBody,
    Skeleton,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {getUrl} from '../../../utils/cloudinary-utils'
import ContentAsset from '../../content-asset'

const sizeGuideSkeleton = () => {
    return (
        <Box margin="30px 0">
            <Skeleton
                width="70px"
                height="30px"
                marginBottom="10px"
                marginLeft={{base: 'auto', lg: '0'}}
                marginRight={{base: 'auto', lg: '0'}}
            />
            <Skeleton
                width="70%"
                height="15px"
                marginBottom="5px"
                marginLeft={{base: 'auto', lg: '0'}}
                marginRight={{base: 'auto', lg: '0'}}
            />
            <Skeleton
                width="30%"
                height="15px"
                marginLeft={{base: 'auto', lg: '0'}}
                marginRight={{base: 'auto', lg: '0'}}
            />
            <Skeleton
                width="70%"
                height="15px"
                marginTop="30px"
                marginBottom="5px"
                marginLeft={{base: 'auto', lg: '0'}}
                marginRight={{base: 'auto', lg: '0'}}
            />
            <Skeleton
                width="30%"
                height="15px"
                marginLeft={{base: 'auto', lg: '0'}}
                marginRight={{base: 'auto', lg: '0'}}
            />
        </Box>
    )
}

const SizeGuide = forwardRef(({sizeChart}, ref) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const drawerBodyRef = useRef()
    const drawerHeaderRef = useRef()

    useImperativeHandle(ref, () => ({open: onOpen}), [onOpen])

    const handleActions = (e) => {
        e.preventDefault()
        if (e.target.getAttribute('href') === '#size-chart') {
            document.querySelector('#sizeChart').classList.add('active')
            document.querySelector('#findYourSize').classList.remove('active')

            document.querySelector('.size-chart-section').classList.add('show')
            document.querySelector('.size-chart-section').classList.add('active')
            document.querySelector('.find-your-size-section').classList.remove('show')
            document.querySelector('.find-your-size-section').classList.remove('active')
        } else if (e.target.getAttribute('href') === '#find-your-size') {
            document.querySelector('#sizeChart').classList.remove('active')
            document.querySelector('#findYourSize').classList.add('active')

            document.querySelector('.find-your-size-section').classList.add('show')
            document.querySelector('.find-your-size-section').classList.add('active')
            document.querySelector('.size-chart-section').classList.remove('show')
            document.querySelector('.size-chart-section').classList.remove('active')
        } else if (e.target.getAttribute('data-target') === '#needHelp') {
            if (e.target.className.indexOf('collapsed') !== -1) {
                e.target.classList.remove('collapsed')
                document.querySelector('#needHelp').classList.add('show')
            } else {
                e.target.classList.add('collapsed')
                document.querySelector('#needHelp').classList.remove('show')
            }
            setTimeout(() => {
                drawerBodyRef?.current?.scrollTo({
                    top: drawerBodyRef.current.scrollHeight,
                    behavior: 'smooth'
                })
            }, 100)
        } else if (
            e?.target?.parentElement?.getAttribute('data-target') === '#needHelp' ||
            e?.target?.parentElement?.className.indexOf('help-details-header') !== -1
        ) {
            if (e.target?.parentElement?.className.indexOf('collapsed') !== -1) {
                e.target.parentElement.classList.remove('collapsed')
                document.querySelector('#needHelp').classList.add('show')
            } else {
                e.target.parentElement.classList.add('collapsed')
                document.querySelector('#needHelp').classList.remove('show')
            }
            setTimeout(() => {
                drawerBodyRef?.current?.scrollTo({
                    top: drawerBodyRef.current.scrollHeight,
                    behavior: 'smooth'
                })
            }, 100)
        } else if (e.target.className.indexOf('right-selector') !== -1) {
            document.querySelector('.right-selector').classList.add('active')
            document.querySelector('.left-selector').classList.remove('active')
            document
                .querySelector('.js-size-chart-table')
                .setAttribute('data-option-selected', 'right')
        } else if (e.target.className.indexOf('left-selector') !== -1) {
            document.querySelector('.right-selector').classList.remove('active')
            document.querySelector('.left-selector').classList.add('active')
            document
                .querySelector('.js-size-chart-table')
                .setAttribute('data-option-selected', 'left')
        } else if (e.target.getAttribute('data-toggle') === 'tab') {
            document
                .querySelectorAll('#ringSizeTabs a')
                ?.forEach((e) => e.classList?.remove('active'))
            e.target.classList.add('active')

            document
                .querySelectorAll('#sizeChart-tabContent .tab-content .tab-pane[role="tabpanel"]')
                ?.forEach((e) => e.classList?.remove('active'))
            document
                .querySelectorAll('.nav-tabs-wrapper .tab-pane[role="tabpanel"]')
                ?.forEach((e) => e.classList?.remove('active'))
            document.querySelector(`${e.target.getAttribute('href')}`).classList?.add('active')
        } else if (
            e.target.getAttribute('href') &&
            (e.target.getAttribute('href')?.indexOf('tel') !== -1 ||
                e.target.getAttribute('href')?.indexOf('mailto') !== -1 ||
                e.target.getAttribute('href')?.indexOf('html') !== -1)
        ) {
            location.href = e.target.getAttribute('href')
        }
    }

    return (
        <Box className="size-guide-wrapper">
            <Drawer
                placement="right"
                isOpen={isOpen}
                onClose={onClose}
                size="lg"
                motionPreset={{base: 'slideInBottom', md: 'slideInRight'}}
                scrollBehavior="inside"
                isFullHeight={isDesktop ? false : true}
                // Add the following 1 line to fix the Safari behavior of Drawer Close
                returnFocusOnClose={false}
            >
                <DrawerOverlay background="blackAlpha600" height="100%" />
                <DrawerContent
                    borderRadius="0px"
                    className="size-guide-content"
                    maxWidth="515px"
                    maxHeight="100%"
                    height="100%"
                    sx={{
                        '& p': {
                            maxWidth: '100%'
                        },
                        '#size-chart li': {
                            listStyleType: 'none'
                        },
                        '& .size-chart-title': {
                            fontFamily: 'heading',
                            color: 'black.100',
                            margin: '20px 0 10px 0',
                            fontWeight: '400',
                            lineHeight: 'normal',
                            fontSize: '29px',
                            letterSpacing: '0.145px',
                            'p:first-of-type': {
                                marginTop: '10px'
                            }
                        },
                        '.img-ctnr': {
                            marginTop: '20px',
                            img: {
                                width: '100%'
                            }
                        },
                        '& .size-chart-description': {
                            fontFamily: 'body',
                            fontSize: '16px',
                            color: 'black.100',
                            fontWeight: '300',
                            margin: '0 0 30px 0',
                            lineHeight: '25px',
                            letterSpacing: '0.08px'
                        },
                        '& .size-chart-wrap': {
                            display: 'grid'
                        },
                        '.size-chart-wrap .size-chart-table .separator': {
                            display: 'none'
                        },
                        '.size-chart-wrap .size-chart-table th:nth-child(2)>img, .size-chart-wrap .size-chart-table th:nth-child(3)>img':
                            {
                                marginTop: '10px',
                                display: 'flex'
                            },
                        '& .size-chart-wrap .size-chart-table td span': {
                            textAlign: 'left',
                            display: 'block'
                        },
                        '& th': {
                            fontSize: '14px',
                            color: 'black.100',
                            fontFamily: 'body',
                            fontWeight: '400',
                            lineHeight: '22px',
                            letterSpacing: '0.05em',
                            alignContent: 'baseline',
                            textAlign: 'left'
                        },
                        '& .size-chart-wrap .size-chart-table th:first-of-type': {
                            display: 'flex'
                        },
                        '& .size-chart-wrap .size-chart-table th:nth-child(2), & .size-chart-wrap .size-chart-table th:nth-child(3)':
                            {
                                position: 'relative',
                                textAlign: 'left'
                            },
                        '& .size-chart-wrap .size-chart-table td': {
                            fontFamily: 'body',
                            letterSpacing: '.005em',
                            color: 'black.100',
                            fontSize: '.875rem',
                            lineHeight: '22px',
                            fontWeight: '300',
                            textAlign: 'left',
                            padding: '10px 0',
                            borderBottom: '1px solid',
                            borderColor: 'gray.800'
                        },
                        '& .tab-pane': {
                            transition: 'all, .15s linear',
                            display: 'none'
                        },
                        '.tab-pane:not(.active)': {
                            opacity: 0
                        },
                        '& .tab-pane.active': {
                            transition: 'all, .15s linear',
                            display: 'block !important'
                        },
                        '& .nav-link': {
                            padding: '10px',
                            width: '50%',
                            justifyContent: 'center',
                            height: 'inherit',
                            color: 'black.100',
                            fontWeight: '600',
                            lineHeight: '22px',
                            letterSpacing: '0.07px',
                            display: 'inline-flex'
                        },
                        '& .nav-link.active': {
                            borderBottom: '5px solid',
                            borderColor: 'black.100'
                        },
                        '& .nav-tabs-indicated': {
                            height: '42px',
                            width: '100%',
                            display: 'flex',
                            borderBottom: '1px solid',
                            borderColor: 'gray.700'
                        },
                        '.find-your-size br': {
                            display: 'none'
                        },
                        '& .size-chart-section': {
                            marginTop: '30px'
                        },
                        '& table': {
                            width: '100%'
                        },
                        '.chart-section': {
                            backgroundColor: 'gray.400',
                            padding: '20px 30px',
                            margin: '30px 0 0 0'
                        },
                        '.accordion-item__header button': {
                            display: 'inline-flex',
                            width: '100%',
                            justifyContent: 'space-between'
                        },
                        '.accordion-item__header span': {
                            display: 'inline-flex',
                            gap: '10px'
                        },
                        '.accordion-component.chart-section .card-header .img-collapse': {
                            transform: 'rotate(180deg)'
                        },
                        '.accordion-component .card-header button .img-collapse': {
                            display: 'block'
                        },
                        '.accordion-component .card-header button.collapsed .img-default': {
                            display: 'block'
                        },
                        '.accordion-component .card-header button.collapsed .img-collapse': {
                            display: 'none'
                        },
                        '.accordion-component .card-header button .img-default': {
                            display: 'none'
                        },
                        '#needHelp.show': {
                            display: 'block'
                        },
                        '#needHelp li::marker': {
                            content: "''"
                        },
                        '.text-link': {
                            textDecoration: 'underline',
                            textUnderlineOffset: 4
                        },
                        '.size-chart-section': {
                            color: 'black.100',
                            fontSize: '16px',
                            fontWeight: 300,
                            lineHeight: '25px',
                            letterSpacing: '0.08px'
                        },
                        '#needHelp': {
                            display: 'none',
                            h3: {
                                color: 'black.100',
                                fontSize: '14px',
                                lineHeight: '22px',
                                letterSpacing: '0.07px',
                                fontWeight: '600',
                                fontFamily: 'body'
                            },
                            '.help-details, .help-details--icon li': {
                                marginBottom: '10px'
                            },
                            'span, li': {
                                color: 'black.100',
                                fontSize: '14px',
                                lineHeight: '22px',
                                letterSpacing: '0.07px',
                                fontWeight: '300',
                                fontFamily: 'body'
                            },
                            ul: {
                                padding: 0,
                                marginBottom: '0px',
                                color: 'black.100',
                                fontSize: '14px',
                                lineHeight: '22px',
                                letterSpacing: '0.07px',
                                fontWeight: '300',
                                fontFamily: 'body'
                            },
                            '.help-details--icon': {
                                marginBottom: '20px !important',
                                '.detail-item': {
                                    display: 'inline-flex'
                                },
                                img: {
                                    marginRight: '10px'
                                }
                            }
                        },
                        '.left-selector, .right-selector': {
                            width: '52px',
                            height: '20px',
                            display: 'block',
                            backgroundColor: 'gray.400',
                            fontSize: '14px',
                            fontWeight: '500',
                            lineHeight: '20px',
                            letterSpacing: '0.1px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            color: 'black.100',
                            borderRadius: '100px'
                        },
                        '.left-selector.active, .right-selector.active': {
                            backgroundColor: 'black.100 !important',
                            color: 'white !important'
                        },
                        '.js-dimension-selector': {
                            display: 'inline-flex',
                            borderRadius: '100px',
                            backgroundColor: 'gray.400'
                        },
                        '.size-chart-wrap--component .size-chart-table.has-selector[data-option-selected=right] .size-right-selector':
                            {
                                display: 'block'
                            },
                        '.size-chart-wrap--component .size-chart-table.has-selector .size-right-selector, .size-chart-wrap--component .size-chart-table.has-selector[data-option-selected=right] .size-left-selector':
                            {
                                display: 'none'
                            },
                        '.float-right': {
                            float: 'inline-end'
                        },
                        '.find-your-size': {
                            p: {
                                marginTop: '20px'
                            },
                            ol: {
                                gap: '20px',
                                marginTop: '20px',
                                marginBottom: '20px',
                                display: 'grid',
                                paddingLeft: '1.5rem'
                            },
                            'li, p': {
                                color: 'black.100',
                                fontSize: '16px',
                                fontWeight: '300',
                                lineHeight: '25px',
                                letterSpacing: '0.08px'
                            },
                            'li::marker': {
                                display: 'list-item'
                            },
                            hr: {
                                margin: '20px 0 0 0',
                                width: '100%',
                                borderColor: 'gray.700'
                            }
                        },
                        '.help-details li': {
                            marginBottom: '0px'
                        },
                        '.help-details-header': {
                            color: 'black.100',
                            fontWeight: '300',
                            fontSize: '14px',
                            lineHeight: '23px',
                            letterSpacing: '0.07px'
                        },
                        '.tbl-footnote': {
                            marginTop: '30px',
                            sup: {
                                top: 0,
                                color: 'blue.600',
                                marginRight: '10px'
                            },
                            span: {
                                fontSize: '.8125rem',
                                color: 'black.100',
                                fontWeight: 300,
                                lineHeight: '22px'
                            }
                        },
                        '.size-chart-wrap--component ul.nav-tabs, #ringSizeTabs.nav-tabs': {
                            marginBottom: '30px',
                            gap: '10px',
                            display: 'inline-flex',
                            '& ::marker': {
                                content: "''"
                            },
                            a: {
                                color: 'black.100',
                                textDecoration: 'underline',
                                textUnderlineOffset: 4,
                                fontSize: '14px',
                                lineHeight: '23px',
                                letterSpacing: '0.07px'
                            },
                            'a.active': {
                                color: 'blue.700',
                                fontWeight: '600',
                                textDecoration: 'none'
                            }
                        }
                    }}
                >
                    <DrawerHeader
                        ref={drawerHeaderRef}
                        boxShadow="none !important"
                        className="size-guide-header"
                        padding="30px 30px 10px 30px !important"
                    >
                        <Box
                            className="close-drawer"
                            display="inline-flex"
                            cursor="pointer"
                            onClick={onClose}
                        >
                            <Image
                                alt="Arrow"
                                width="15px"
                                margin="4px 10px 0 0"
                                height="16px"
                                src={getUrl('UIImages/images/left-arrow-icon.svg')}
                            />
                            <Text
                                color="black.100"
                                fontSize="16px"
                                fontWeight="400"
                                lineHeight="25px"
                                letterSpacing="0.08px"
                            >
                                <FormattedMessage
                                    id="return.product.view"
                                    defaultMessage="Return to Product View"
                                />
                            </Text>
                        </Box>
                    </DrawerHeader>
                    <DrawerBody
                        className="modal-size-guide"
                        onScroll={(e) => {
                            if (e?.target?.scrollTop) {
                                drawerHeaderRef?.current?.classList?.add('static')
                            } else {
                                drawerHeaderRef?.current?.classList?.remove('static')
                            }
                        }}
                        ref={drawerBodyRef}
                        padding="0 30px 30px 30px !important"
                        onClick={handleActions}
                        sx={{
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                display: 'none'
                            },
                            '&::-webkit-scrollbar-track': {
                                display: 'none'
                            }
                        }}
                    >
                        <ContentAsset
                            contentAssetId={sizeChart}
                            width="100%"
                            skeleton={sizeGuideSkeleton}
                        />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    )
})

SizeGuide.displayName = 'SizeGuide'

SizeGuide.propTypes = {
    sizeChart: PropTypes.string
}

export default React.memo(SizeGuide)
